/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { notification } from 'antd';
import { REQUEST_TYPE_ACCESS, REQUEST_TYPE_GUARDING } from 'core/utils/value';
import { VisitorRequestReview, VisitRequestReviewState, DataProps, CheckSessionProps, AddCommentProps, FetchApprovers, PushSuccess, ScheduleSuccess, ReleaseSuccess } from './types';
import { getVisitRequestReview, resubmitActionRequest, updateVisitRequestReview, checkRequestSession, overruleSession, addComment, fetchApprovers, pushToApprover, scheduleRequest, releaseSession, approveNsipVisitor } from './visitRequestReviewApi';

const emptyVisitRequestState: VisitorRequestReview = {
    id: null,
    encryptedId: '',
    site: null,
    isRetail: false,
    approvers: [],
    requester: null,
    approver: null,
    costApproverByPass: false,
    costApprover: null,
    hasCostApprove: false,
    status: '',
    currentStatusStep: 0,
    shifts: null,
    visitors: null,
    visitorsNumber: 0,
    costCode: null,
    dates: null,
    dateAndTime: null,
    siteAccess: null,
    workTypeName: null,
    workTypes: null,
    workDetails: null,
    workDescription: null,
    comments: [],
    totalHours: 0,
    declaration: {
        signature: null,
        agreed: false,
    },
    availableApprovers: [],
    pushActionData: {
        defaultApprover: null,
        parentWorkType: null,
        childrenWorkTypes: null,
    },
    dateSubmitted: '',
    securityOfficers: [],
    attachments: [],
    lastCheckedIn: [],
    type: null,
};

const initialState = {
    error: null,
    isFetching: false,
    isUpdating: false,
    isCreating: false,
    isCheckingSession: false,
    isFetchingApprovers: false,
    isScheduling: false,
    isPushing: false,
    isReleasing: false,
    isApprovingNsip: false,
    lockedStatus: {
        isLocked: false,
        lockedBy: '',
        overriden: false,
        requestId: null,
    },
    performingAction: false,
    visitRequestReview: emptyVisitRequestState,
    statusData: {
        currentStatusStep: 0,
        inActiveActions: {},
    },
    metadata: {
        nextRequestId: null,
        pastRequestId: null,
    },
    canAmend: false,
    success: false,
} as VisitRequestReviewState;

export const visitRequestReviewSlice = createSlice({
    name: 'visitRequestReview',
    initialState,
    reducers: {
        resetLockedStatus: (state: VisitRequestReviewState) => {
            state.lockedStatus = {
                isLocked: false,
                lockedBy: '',
                overriden: false,
                requestId: null,
            };
        },
    },
    extraReducers: (builder) => {
        // Load visit request review data
        builder
            .addCase(getVisitRequestReview.pending, (state: VisitRequestReviewState) => {
                state.isFetching = true;
                state.error = null;
                state.lockedStatus = {
                    isLocked: false,
                    lockedBy: '',
                    overriden: false,
                    requestId: null,
                };
            });
        builder
            .addCase(getVisitRequestReview.fulfilled, (
                state: VisitRequestReviewState,
                action: PayloadAction<DataProps>,
            ) => {
                state.isFetching = false;
                state.visitRequestReview = action.payload.visitRequest;
                state.statusData = action.payload.statusData;
                state.metadata = action.payload.metadata;
                state.lockedStatus.isLocked = action.payload.lockedData.isLocked;
                state.lockedStatus.lockedBy = action.payload.lockedData.lockedBy;
                state.canAmend = action.payload.canAmend ?? false;
            });
        builder
            .addCase(getVisitRequestReview.rejected, (
                state: VisitRequestReviewState,
                action: object,
            ) => {
                state.isFetching = false;
                state.error = action;
                state.visitRequestReview = emptyVisitRequestState;
            });

        // Update visit request review data
        builder
            .addCase(updateVisitRequestReview.pending, (state: VisitRequestReviewState) => {
                state.isUpdating = true;
                state.error = null;
                state.success = false;
            });
        builder
            .addCase(updateVisitRequestReview.fulfilled, (
                state: VisitRequestReviewState,
                action: PayloadAction<DataProps>, // here
            ) => {
                state.isUpdating = false;
                state.visitRequestReview = action.payload.visitRequest;
                state.statusData = action.payload.statusData;
                if (action.payload.metadata) {
                    state.metadata = action.payload.metadata;
                }
                state.success = true;
            });
        builder
            .addCase(updateVisitRequestReview.rejected, (
                state: VisitRequestReviewState,
                action: object,
            ) => {
                state.isUpdating = false;
                state.error = action;
                state.success = false;
            });

        // Resubmit action
        builder
            .addCase(resubmitActionRequest.pending, (state: VisitRequestReviewState) => {
                state.performingAction = true;
                state.error = null;
            });
        builder
            .addCase(resubmitActionRequest.fulfilled, (
                state: VisitRequestReviewState,
                action: PayloadAction<{ type?: string }>,
            ) => {
                state.performingAction = false;
                let label = 'visit request';
                if (action.payload?.type === REQUEST_TYPE_ACCESS) label = 'access request';
                else if (action.payload?.type === REQUEST_TYPE_GUARDING) label = 'guarding request';
                notification.success({
                    message: 'Success',
                    description: `The ${label} has been copied for resubmission.`,
                });
            });
        builder
            .addCase(resubmitActionRequest.rejected, (
                state: VisitRequestReviewState,
                action: object,
            ) => {
                state.performingAction = false;
                state.error = action;
            });

        // Check request session
        builder
            .addCase(checkRequestSession.pending, (state: VisitRequestReviewState) => {
                state.isCheckingSession = true;
                state.error = null;
                state.lockedStatus.overriden = false;
            });
        builder
            .addCase(checkRequestSession.fulfilled, (
                state: VisitRequestReviewState,
                action: PayloadAction<CheckSessionProps>,
            ) => {
                state.lockedStatus.isLocked = false;
                state.isCheckingSession = false;

                state.lockedStatus.lockedBy = action.payload.overruledBy;
                state.lockedStatus.overriden = !action.payload.success;
                state.lockedStatus.requestId = state.visitRequestReview.id;
            });
        builder
            .addCase(checkRequestSession.rejected, (
                state: VisitRequestReviewState,
                action: object,
            ) => {
                state.isCheckingSession = false;
                state.error = action;
            });

        // Overrule Session:
        builder
            .addCase(overruleSession.pending, (state: VisitRequestReviewState) => {
                state.isFetching = true;
                state.error = null;
            });
        builder
            .addCase(overruleSession.fulfilled, (
                state: VisitRequestReviewState,
                action: PayloadAction<CheckSessionProps>,
            ) => {
                state.isFetching = false;
                state.error = null;
                if (!action.payload.success) {
                    state.lockedStatus.isLocked = false;
                    state.lockedStatus.lockedBy = '';
                }
            });
        builder
            .addCase(overruleSession.rejected, (
                state: VisitRequestReviewState,
                action: object,
            ) => {
                state.isFetching = false;
                state.error = action;
            });

        // Fetching approvers:
        builder
            .addCase(fetchApprovers.pending, (state: VisitRequestReviewState) => {
                state.isFetchingApprovers = true;
                state.error = null;
            });
        builder
            .addCase(fetchApprovers.fulfilled, (
                state: VisitRequestReviewState,
                action: PayloadAction<FetchApprovers>,
            ) => {
                state.isFetchingApprovers = false;
                state.visitRequestReview.availableApprovers = action.payload.approvers;
            });
        builder
            .addCase(fetchApprovers.rejected, (
                state: VisitRequestReviewState,
                action: object,
            ) => {
                state.isFetchingApprovers = false;
                state.error = action;
            });

        // Push to approver:
        builder
            .addCase(pushToApprover.pending, (state: VisitRequestReviewState) => {
                state.isPushing = true;
                state.error = null;
            });
        builder
            .addCase(pushToApprover.fulfilled, (
                state: VisitRequestReviewState,
                action: PayloadAction<PushSuccess>,
            ) => {
                state.isPushing = false;
                state.visitRequestReview.approvers = action.payload.approvers;

                notification.success({
                    message: 'Success',
                    description: action.payload.message,
                });
            });
        builder
            .addCase(pushToApprover.rejected, (
                state: VisitRequestReviewState,
                action: object,
            ) => {
                state.isPushing = false;
                state.error = action;
            });

        // add comment:
        builder
            .addCase(addComment.pending, (state: VisitRequestReviewState) => {
                state.isFetching = true;
                state.error = null;
            });
        builder
            .addCase(addComment.fulfilled, (
                state: VisitRequestReviewState,
                action: PayloadAction<AddCommentProps>,
            ) => {
                state.isFetching = false;
                state.visitRequestReview = action.payload.visitRequest;
            });
        builder
            .addCase(addComment.rejected, (
                state: VisitRequestReviewState,
                action: object,
            ) => {
                state.isFetching = false;
                state.error = action;
            });

        // Schedule:
        builder
            .addCase(scheduleRequest.pending, (state: VisitRequestReviewState) => {
                state.isScheduling = true;
                state.error = null;
            });
        builder
            .addCase(scheduleRequest.fulfilled, (
                state: VisitRequestReviewState,
                action: PayloadAction<ScheduleSuccess>,
            ) => {
                state.isScheduling = false;
                const { dates, status, statusData, totalHours } = action.payload;

                // Update dates with the new array from backend
                if (dates && Array.isArray(dates)) {
                    state.visitRequestReview.dates = dates;
                }

                // Reflect the recomputed billable hours straight away.
                if (totalHours !== undefined) {
                    state.visitRequestReview.totalHours = totalHours;
                }

                state.visitRequestReview.status = status;
                state.statusData = statusData;

                notification.success({
                    message: 'Success',
                    description: action.payload.message,
                });
            });
        builder
            .addCase(scheduleRequest.rejected, (
                state: VisitRequestReviewState,
                action: object,
            ) => {
                state.isScheduling = false;
                state.error = action;
            });

        // Approve NSIP Visitor:
        builder
            .addCase(approveNsipVisitor.pending, (state: VisitRequestReviewState) => {
                state.isApprovingNsip = true;
                state.error = null;
            });
        builder
            .addCase(approveNsipVisitor.fulfilled, (
                state: VisitRequestReviewState,
                action: PayloadAction<{ visitRequest: VisitorRequestReview, statusData: DataProps['statusData'] }>,
            ) => {
                state.isApprovingNsip = false;
                state.visitRequestReview = action.payload.visitRequest;
                state.statusData = action.payload.statusData;
            });
        builder
            .addCase(approveNsipVisitor.rejected, (
                state: VisitRequestReviewState,
                action: object,
            ) => {
                state.isApprovingNsip = false;
                state.error = action;
            });

        // Release:
        builder
            .addCase(releaseSession.pending, (state: VisitRequestReviewState) => {
                state.isReleasing = true;
                state.error = null;
            });
        builder
            .addCase(releaseSession.fulfilled, (
                state: VisitRequestReviewState,
                action: PayloadAction<ReleaseSuccess>,
            ) => {
                state.isReleasing = !(action.payload.success);
            });
        builder
            .addCase(releaseSession.rejected, (
                state: VisitRequestReviewState,
                action: object,
            ) => {
                state.isReleasing = false;
                state.error = action;
            });
    },
});

export default visitRequestReviewSlice.reducer;
export const { resetLockedStatus } = visitRequestReviewSlice.actions;
